import { Resource } from 'react-admin'
import type { ResourceProps } from 'react-admin'
import type { Permissions } from '../types/apiTypes'
import certificates from './Certificates'
import municipality from './Municipality'
import organizations from './Organizations'
import type { ReactElement } from 'react'
import { CERTIFICATE_ORGANIZATION_WRITE_SCOPE, CERTIFICATE_READ_SCOPE } from '../config'

type ScopableResource = ResourceProps & {
  name: string
  options: {
    label: string
    scopes?: string[]
    /** Menu group; 'settings' = under "Instellingen". */
    submenu?: string
  }
}

const allResourceDefs = (): ScopableResource[] => [
  {
    name: 'certificate',
    options: { label: 'resources.certificates.list', scopes: [CERTIFICATE_READ_SCOPE] },
    list: certificates.list,
    edit: certificates.edit,
    show: certificates.show,
    create: certificates.create,
    icon: certificates.icon,
  },
  {
    name: 'municipality',
    options: { label: 'resources.municipality.list', scopes: [CERTIFICATE_READ_SCOPE], submenu: 'settings' },
    list: municipality.list,
    edit: municipality.edit,
    show: municipality.show,
    create: municipality.create,
    icon: municipality.icon,
  },
  {
    name: 'organization',
    options: { label: 'resources.organization.list', scopes: [CERTIFICATE_ORGANIZATION_WRITE_SCOPE], submenu: 'settings' },
    list: organizations.list,
    edit: organizations.edit,
    show: organizations.show,
    create: organizations.create,
    icon: organizations.icon,
  },
  {
    name: 'instances',
    options: { label: 'Gemeenten', scopes: [CERTIFICATE_READ_SCOPE] },
  },
  {
    name: 'interfaces',
    options: { label: 'Interfaces', scopes: [CERTIFICATE_READ_SCOPE] },
  },
  {
    name: 'auditlog',
    options: { label: 'Audit Logs', scopes: [CERTIFICATE_READ_SCOPE] },
  },
]

const fallbackDefs = (): ScopableResource[] => [
  {
    name: 'certificaten',
    options: { label: 'Certificaten' },
    list: certificates.list,
    icon: certificates.icon,
  },
]

const toElements = (defs: ScopableResource[]): ReactElement[] =>
  defs.map(def => <Resource key={def.name} {...def} />)

export const resourcesForScopes = ({ scopes }: Permissions): ReactElement[] => {
  if (!scopes || scopes.length === 0) {
    console.warn('No scopes received, returning fallback resource')
    return toElements(fallbackDefs())
  }

  const defs = allResourceDefs().filter(def => {
    const defScopes = def.options.scopes ?? []
    return defScopes.some(scope => scopes.includes(scope))
  })

  if (defs.length === 0) {
    console.warn('No resources received, returning fallback resource')
    return toElements(fallbackDefs())
  }

  return toElements(defs)
}
